import axios from '@/libs/axios'

// These keys are the UI/backend integration boundary. Upstream classifier names
// intentionally do not appear in the UI.
export const CUSTOMS_CLASSIFIER_KEYS = Object.freeze({
  supportingDocumentTypes: 'supporting_document_types',
  transportDocumentTypes: 'transport_document_types',
  previousDocumentTypes: 'previous_document_types',
  countries: 'countries',
  currencies: 'currencies',
  measurementUnits: 'measurement_units',
  measurementQualifiers: 'measurement_qualifiers',
  incoterms: 'incoterms',
  fiscalReferenceRoles: 'fiscal_reference_roles',
  additionDeductionCodes: 'addition_deduction_codes',
  procedures: 'procedures',
  additionalProcedures: 'additional_procedures',
  packageTypes: 'package_types',
  transportModes: 'transport_modes',
  transactionNatures: 'transaction_natures',
  transportIdentificationTypes: 'transport_identification_types',
  valuationMethods: 'valuation_methods',
  locationTypes: 'location_types',
  locationIdentificationQualifiers: 'location_identification_qualifiers',
  polishAttachmentDocuments: 'polish_attachment_documents',
})

// Kept alongside the keys so additions to H1 selectors have one reviewable map.
export const H1_CLASSIFIER_FIELDS = Object.freeze({
  supportingDocumentTypes: ['supporting_documents[].document_type', 'items[].supporting_documents[].document_type'],
  transportDocumentTypes: ['transport_documents[].transport_document_type'],
  countries: ['transport_data.*_country', 'delivery_terms.trade_terms_country', 'parties.*.country', 'items[].goods_data.*_country'],
  currencies: ['transaction_data.invoice_currency', 'transport_cost_currency', 'additions_deductions[].currency', 'supporting_documents[].currency'],
  measurementUnits: ['supplementary_units[].supplementary_unit', 'supporting_documents[].supplementary_measurement_unit'],
  measurementQualifiers: ['supplementary_units[].measurement_qualifier', 'supporting_documents[].measurement_qualifier'],
  incoterms: ['delivery_terms.trade_terms_condition'],
  fiscalReferenceRoles: ['additional_fiscal_references[].role'],
  additionDeductionCodes: ['costs_distribution_data.additions_deductions[].code', 'items[].additions_deductions[].code'],
  procedures: ['items[].goods_data.current_procedure', 'items[].goods_data.previous_procedure'],
  additionalProcedures: ['items[].goods_data.additional_procedure'],
  packageTypes: ['items[].packaging[].type_of_packages'],
  transportModes: ['transport_data.arrival_transport_means_mode', 'transport_data.border_transport_means_mode'],
  transactionNatures: ['transaction_data.transaction_nature', 'items[].transaction_data.transaction_nature'],
  transportIdentificationTypes: ['transport_data.arrival_transport_means_identification_type'],
  valuationMethods: ['items[].transaction_data.customs_valuation_method'],
  locationTypes: ['warehouse.type'],
  locationIdentificationQualifiers: ['warehouse.qualifier'],
})

const CACHE_TTL = 5 * 60 * 1000
const cache = new Map()

// The backend selects a country variant or the generic snapshot. Keep every
// country in the request and cache identity, including newly synced variants.
export const classifierCountryParam = country => (
  country ? String(country).trim().toUpperCase() || null : null
)

// A keyword narrows the served list, so it is part of the cache identity: two
// search terms for the same key must never overwrite one another. The prefix
// keeps a keyword from colliding with a country segment.
const classifierKeyword = keyword => {
  const trimmed = typeof keyword === 'string' ? keyword.trim() : ''
  return trimmed === '' ? null : trimmed
}

// Country variants differ, so the country is part of the cache key.
const classifierCacheKey = (key, country, keyword = null) => {
  const base = country ? `${key}:${country}` : key
  return keyword ? `${base}:keyword=${keyword}` : base
}

// The canonical option shape: { code, label, parentCode, attributes, flags }.
// `flags` carries the behaviour the backend attaches to an option (only previous
// document types currently send any: confirm, require_number_of_items) and
// defaults to {} so a consumer can read a flag without guarding.
export const normalizeClassifierOption = option => ({
  code: String(option.code),
  label: String(option.label),
  parentCode: option.parentCode || null,
  attributes: Array.isArray(option.attributes) ? option.attributes : [],
  flags: option.flags && typeof option.flags === 'object' ? option.flags : {},
})

export const preservedClassifierOption = code => ({
  code: String(code),
  label: 'previously saved value',
  parentCode: null,
  attributes: [],
  flags: {},
  preserved: true,
})

// The one rendering every classifier dropdown uses: `code — label`, or the label
// alone when the code is an empty string, as NL's "No previous Document" entry
// is served.
export const formatClassifierOption = option => (
  option.code === '' || option.code === null || option.code === undefined
    ? option.label
    : `${option.code} — ${option.label}`
)

// The X, Y and Z previous document categories are a fixed rule mirrored by the
// backend's `in:X,Y,Z` validation, not a synced code list, so they stay a
// frontend constant — defined once here and rendered through the shared
// formatter, so the send, resend and editor screens all read them as code plus
// description like every served list.
export const PREVIOUS_DOCUMENT_CATEGORIES = Object.freeze([
  { code: 'X', label: 'Summary Declaration' },
  { code: 'Y', label: 'Initial Declaration' },
  { code: 'Z', label: 'Previous Document' },
].map(option => Object.freeze(normalizeClassifierOption(option))))

export const getClassifierSnapshot = (key, { force = false, country = null, keyword = null } = {}) => {
  country = classifierCountryParam(country)
  const searchTerm = classifierKeyword(keyword)
  const cacheKey = classifierCacheKey(key, country, searchTerm)
  const cached = cache.get(cacheKey)
  const isFresh = cached && cached.snapshot && Date.now() - cached.loadedAt < CACHE_TTL

  if (!force && isFresh) {
    return Promise.resolve(cached.snapshot)
  }
  if (!force && cached && cached.promise) {
    return cached.promise
  }

  // Country is a path segment: /v1/customs/classifiers/{key}/codes/{country}.
  // No fallback to the generic snapshot on 503: for keys with a country variant
  // the generic list carries codes the declaring authority rejects (e.g. 13 of
  // EMTA's 21 transport modes are not in Tulli CL018), so the error propagates
  // and ClassifierSelect shows its error/Retry state until the variant is synced.
  const path = country
    ? `/v1/customs/classifiers/${key}/codes/${country}`
    : `/v1/customs/classifiers/${key}/codes`
  // The endpoint filters the snapshot by code or description when a keyword is
  // given, which keeps long lists (Polish attachment documents) searchable.
  const url = searchTerm ? `${path}?keyword=${encodeURIComponent(searchTerm)}` : path
  // The entry this request owns. A forced refresh replaces it, so every write
  // below checks that the entry is still the cached one: an older, slower
  // request must neither overwrite a newer snapshot nor delete it on failure.
  const entry = { snapshot: cached?.snapshot || null, loadedAt: cached?.loadedAt || 0, promise: null }
  const promise = axios.get(url)
    .then(response => ({
      ...response.data.data,
      options: (response.data.data.options || []).map(normalizeClassifierOption),
    }))
    .then(snapshot => {
      if (cache.get(cacheKey) === entry) {
        entry.snapshot = snapshot
        entry.loadedAt = Date.now()
        entry.promise = null
      }
      return snapshot
    })
    .catch(error => {
      if (cache.get(cacheKey) === entry) {
        cache.delete(cacheKey)
      }
      throw error
    })

  entry.promise = promise
  cache.set(cacheKey, entry)
  return promise
}

// Keyword search is the same snapshot request with a filter, so it shares the
// cache, the request joining and the failure handling of every other list.
export const searchClassifierCodes = (key, keyword, { force = false, country = null } = {}) => (
  getClassifierSnapshot(key, { force, country, keyword })
)

// The canonical previous document type options, for a screen that applies the
// option flags (confirm, require_number_of_items) rather than rendering a
// dropdown. It shares the cache with the dropdown showing the same list, so
// asking for it alongside one costs no extra request.
export const getPreviousDocumentTypes = (country, { force = false } = {}) => {
  const resolvedCountry = classifierCountryParam(country)
  if (!resolvedCountry) return Promise.resolve([])
  return getClassifierSnapshot(CUSTOMS_CLASSIFIER_KEYS.previousDocumentTypes, { force, country: resolvedCountry })
    .then(snapshot => snapshot.options)
}
